import { MapPin, Clock, Instagram, ExternalLink } from 'lucide-react'
import { useLanguage, commonTranslations } from '@/hooks/useLanguage'
import { useConfig } from '@/hooks/useMenuData'
import { getTextAlign } from '@/lib/rtl'
import type { Config, Language } from '@/types'

// ============================================================================
// Types
// ============================================================================

interface FooterProps {
  className?: string
}

// ============================================================================
// Footer Section Component
// ============================================================================

interface FooterSectionProps {
  title: string
  children: React.ReactNode
  isRTL: boolean
}

function FooterSection({ title, children, isRTL }: FooterSectionProps) {
  return (
    <div className={`flex flex-col gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
      <h3 className="font-semibold text-primary text-sm uppercase tracking-wide">
        {title}
      </h3>
      {children}
    </div>
  )
}

// ============================================================================
// Footer Link Component
// ============================================================================

interface FooterLinkProps {
  href: string
  children: React.ReactNode
  external?: boolean
  isRTL: boolean
}

function FooterLink({ href, children, external = false, isRTL }: FooterLinkProps) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors touch-feedback"
    >
      {children}
      {external && (
        <ExternalLink size={14} className={isRTL ? 'transform scale-x-[-1]' : ''} />
      )}
    </a>
  )
}

// ============================================================================
// Powered By Component
// ============================================================================

interface PoweredByProps {
  show: boolean
  language: Language
}

function PoweredBy({ show, language }: PoweredByProps) {
  if (!show) return null

  return (
    <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
      <span>{commonTranslations.powered_by[language]}</span>
      <a
        href="https://omnirise.dev"
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold text-primary hover:text-accent transition-colors"
      >
        OmniRise
      </a>
    </div>
  )
}

// ============================================================================
// Hours Display Component
// ============================================================================

interface HoursDisplayProps {
  hours: Array<{ day: string; time: string }>
  isRTL: boolean
}

function HoursDisplay({ hours, isRTL }: HoursDisplayProps) {
  return (
    <div className="space-y-1">
      {hours.map((hour, index) => (
        <div
          key={index}
          className={`flex gap-2 text-sm text-muted-foreground ${
            isRTL ? 'flex-row-reverse text-right' : 'text-left'
          }`}
        >
          <span className="font-medium min-w-[80px]">{hour.day}:</span>
          <span>{hour.time}</span>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// Footer Component
// ============================================================================

export function Footer({ className = '' }: FooterProps) {
  const { language, isRTL, textDirection } = useLanguage()
  const config = useConfig()

  if (!config) return null

  const { contact, features } = config

  // Get translations
  const t = {
    hours: commonTranslations.hours[language as 'fr' | 'ar' | 'en'] || commonTranslations.hours.fr,
    address: commonTranslations.address[language as 'fr' | 'ar' | 'en'] || commonTranslations.address.fr,
    find_on_maps: language === 'fr' ? 'Trouver sur la carte' : language === 'ar' ? 'البحث في الخريطة' : 'Find on map',
  }

  return (
    <footer
      className={`bg-white border-t border-secondary/30 mt-auto ${className}`}
      dir={textDirection}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-[calc(4.5rem+env(safe-area-inset-bottom))]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Hours Section */}
          <FooterSection title={t.hours} isRTL={isRTL}>
            <div className="flex items-start gap-3">
              <Clock size={18} className="text-primary mt-1 flex-shrink-0" />
              <HoursDisplay hours={contact.hours} isRTL={isRTL} />
            </div>
          </FooterSection>

          {/* Address Section */}
          <FooterSection title={t.address} isRTL={isRTL}>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-primary mt-1 flex-shrink-0" />
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {contact.address}
                </p>
              </div>
              {contact.mapsLink && (
                <FooterLink
                  href={contact.mapsLink}
                  external
                  isRTL={isRTL}
                >
                  <span className="text-sm font-medium">{t.find_on_maps}</span>
                </FooterLink>
              )}
            </div>
          </FooterSection>

          {/* Social/Contact Section */}
          <FooterSection
            title={language === 'fr' ? 'Suivez-nous' : language === 'ar' ? 'تابعنا' : 'Follow us'}
            isRTL={isRTL}
          >
            <div className="flex items-center gap-4">
              {contact.instagram && (
                <a
                  href={`https://instagram.com/${contact.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/50 hover:bg-primary hover:text-white transition-all touch-feedback"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
              )}
              {contact.phone && (
                <a
                  href={`tel:${contact.phone}`}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary/50 hover:bg-primary hover:text-white transition-all touch-feedback"
                  aria-label="Call"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </a>
              )}
            </div>
          </FooterSection>
        </div>

        {/* Powered By */}
        <div className="pt-6 pb-6 border-t border-secondary/30">
          <PoweredBy show={features.poweredByOmniRise} language={language} />
        </div>
      </div>
    </footer>
  )
}
